import { CompleteKundali, Language, PlanetPosition, HouseInfo, BirthDetails } from '../types/kundali';
import { calculateShadbala, getBirthUtcDate, ZODIAC_SIGNS, getSavByBhava } from './vedicEngine';
import { formatOrdinal } from './dateUtils';
import {
  AstrologicalEvidenceItem,
  EvidenceClassification,
  LifeAreaEvidenceMatrix,
  PredictionFingerprint
} from '../types/predictionDifferentiation';
import { LifeArea } from './integratedProfileGenerator';

// ============================================================================
// 1. QUESTION & EVENT CLASSIFICATION TYPES & ENGINE
// ============================================================================

export type PrimaryDomain =
  | 'personality'
  | 'career'
  | 'business'
  | 'wealth'
  | 'education'
  | 'marriage'
  | 'relationship'
  | 'progeny'
  | 'health'
  | 'property'
  | 'vehicle'
  | 'foreignTravel'
  | 'spirituality'
  | 'family'
  | 'litigation'
  | 'timing'
  | 'remedies'
  | 'gemstones'
  | 'dasha'
  | 'transit'
  | 'yoga_dosha'
  | 'general';

export type EventType =
  | 'JOB_CHANGE'
  | 'NEW_JOB'
  | 'PROMOTION'
  | 'GOVERNMENT_JOB'
  | 'CAREER_DECISION'
  | 'BUSINESS_STARTUP'
  | 'CAREER_PATH_COMPARISON'
  | 'CAREER_OVERVIEW'
  | 'MARRIAGE_TIMING'
  | 'MARRIAGE_OVERVIEW'
  | 'PROPERTY_PURCHASE'
  | 'VEHICLE_PURCHASE'
  | 'FOREIGN_RELOCATION'
  | 'CHILDBIRTH'
  | 'CHILDREN_EDUCATION'
  | 'WEALTH_TIMING'
  | 'WEALTH_OVERVIEW'
  | 'HEALTH_RECOVERY'
  | 'HEALTH_OVERVIEW'
  | 'EDUCATION_EXAM'
  | 'GENERAL_OVERVIEW';

export type QuestionType =
  | 'PROMISE'
  | 'TIMING'
  | 'DECISION'
  | 'OBSTACLE'
  | 'REMEDY'
  | 'COMPARISON'
  | 'GENERAL';

export interface CandidateTimingWindow {
  label: string;
  dateRange: string;
  dashaLord: string;
  antardashaLord: string;
  transitSupport: string;
  score: number;
  activationSummary: string;
}

export interface EventTimingAnalysis {
  eventType: EventType;
  eventName: string;
  isTimingQuestion: boolean;
  natalPromise: string;
  currentActivation: string;
  d10Refinement: string;
  candidateWindow1: CandidateTimingWindow | null;
  candidateWindow2: CandidateTimingWindow | null;
  strongestWindow: string;
  secondaryWindow: string | null;
  doubleTransitVerdict: string;
  delaysAndQualifications: string[];
  practicalGuidance: string[];
  relevanceFilterApplied: boolean;
}

export interface QuestionClassification {
  primaryDomain: PrimaryDomain;
  secondaryDomain: PrimaryDomain | null;
  eventType: EventType;
  questionType: QuestionType;
  isTimingRequested: boolean;
  referencedYear: number | null;
  referencedPlanet: string | null;
  referencedHouse: number | null;
  referencedEvent: string | null;
  isFollowUp: boolean;
  inheritedDomain: PrimaryDomain | null;
  inheritedEventType: EventType | null;
  isRemedyRequested: boolean;
  requiresSpecializedMethod: 'PARASHARI' | 'JAIMINI' | 'KP' | 'DIVISIONAL_VARGA' | 'GENERAL';
  isChildGated: boolean;
  userQuery: string;
}

const SIGN_LORDS: Record<string, string> = {
  Aries: 'Mars',
  Taurus: 'Venus',
  Gemini: 'Mercury',
  Cancer: 'Moon',
  Leo: 'Sun',
  Virgo: 'Mercury',
  Libra: 'Venus',
  Scorpio: 'Mars',
  Sagittarius: 'Jupiter',
  Capricorn: 'Saturn',
  Aquarius: 'Saturn',
  Pisces: 'Jupiter'
};

export function getSignLord(sign: string): string {
  return SIGN_LORDS[sign] || 'Jupiter';
}

/**
 * Question-First Classifier: Dissects the user's intent into explicit life domain,
 * specific event trigger, and timing/decision nature with follow-up memory.
 */
export function classifyAstrologicalQuestion(
  message: string,
  chatHistory: Array<{ sender: string; text: string }> = [],
  nativeAge: number | null = null
): QuestionClassification {
  const q = (message || '').trim();
  const qLower = q.toLowerCase();

  // 1. Check for explicit domain markers in current message first
  const hasExplicitVehicle =
    qLower.includes('vehicle') ||
    qLower.includes('car') ||
    qLower.includes('bike') ||
    qLower.includes('scooter') ||
    qLower.includes('motorcycle') ||
    qLower.includes('automobile') ||
    qLower.includes('conveyance') ||
    qLower.includes('vahana') ||
    qLower.includes('गाड़ी') ||
    qLower.includes('वाहन') ||
    qLower.includes('कार') ||
    qLower.includes('स्कूटर') ||
    qLower.includes('मोटरसाइकिल');

  const hasExplicitProperty =
    qLower.includes('house') ||
    qLower.includes('flat') ||
    qLower.includes('plot') ||
    qLower.includes('land') ||
    qLower.includes('real estate') ||
    qLower.includes('apartment') ||
    qLower.includes('मकान') ||
    qLower.includes('घर') ||
    qLower.includes('जमीन') ||
    qLower.includes('प्लॉट') ||
    qLower.includes('फ्लैट') ||
    qLower.includes('सम्पत्ति');

  const hasExplicitCareer =
    qLower.includes('job') ||
    qLower.includes('career') ||
    qLower.includes('profession') ||
    qLower.includes('promotion') ||
    qLower.includes('salary') ||
    qLower.includes('interview') ||
    qLower.includes('boss') ||
    qLower.includes('office') ||
    qLower.includes('नौकरी') ||
    qLower.includes('करियर') ||
    qLower.includes('पदोन्नति') ||
    qLower.includes('प्रमोशन') ||
    qLower.includes('व्यवसाय') ||
    qLower.includes('व्यापार');

  const hasExplicitMarriage =
    qLower.includes('marriage') ||
    qLower.includes('spouse') ||
    qLower.includes('wedding') ||
    qLower.includes('husband') ||
    qLower.includes('wife') ||
    qLower.includes('marital') ||
    qLower.includes('विवाह') ||
    qLower.includes('शादी') ||
    qLower.includes('जीवनसाथी');

  const hasExplicitHealth =
    qLower.includes('health') ||
    qLower.includes('disease') ||
    qLower.includes('illness') ||
    qLower.includes('hospital') ||
    qLower.includes('surgery') ||
    qLower.includes('स्वास्थ्य') ||
    qLower.includes('रोग') ||
    qLower.includes('बीमारी');

  const hasExplicitTravel =
    qLower.includes('abroad') ||
    qLower.includes('foreign') ||
    qLower.includes('visa') ||
    qLower.includes('overseas') ||
    qLower.includes('विदेश');

  const hasExplicitWealth =
    qLower.includes('wealth') ||
    qLower.includes('money') ||
    qLower.includes('finance') ||
    qLower.includes('investment') ||
    qLower.includes('धन') ||
    qLower.includes('पैसे') ||
    qLower.includes('रुपये');

  const hasAnyExplicitDomain =
    hasExplicitVehicle ||
    hasExplicitProperty ||
    hasExplicitCareer ||
    hasExplicitMarriage ||
    hasExplicitHealth ||
    hasExplicitTravel ||
    hasExplicitWealth;

  // 2. Follow-Up Memory & Context Inheritance (Only if no explicit new domain is specified)
  let isFollowUp = false;
  let inheritedDomain: PrimaryDomain | null = null;
  let inheritedEventType: EventType | null = null;

  const followUpTriggers = [
    'what about', 'and what about', 'what of', 'how about', 'which year', 'when will',
    'is 202', 'in 202', 'for 202', 'during 202', 'should i change', 'should i start',
    'what is the main obstacle', 'what are the obstacles', 'any remedy', 'any remedies',
    'tell me more', 'elaborate', 'what next', 'aur batao', 'aur batayein',
    'क्या 202', 'कब होगा', 'कैसा रहेगा', 'उपाय क्या है', '2026 में', '2027 में', 'और बताएं'
  ];

  if (!hasAnyExplicitDomain && followUpTriggers.some(t => qLower.includes(t))) {
    if (chatHistory && chatHistory.length > 0) {
      isFollowUp = true;
      for (let i = chatHistory.length - 1; i >= 0; i--) {
        const prev = chatHistory[i].text.toLowerCase();
        if (prev.includes('vehicle') || prev.includes('car') || prev.includes('bike') || prev.includes('वाहन') || prev.includes('गाड़ी')) {
          inheritedDomain = 'vehicle';
          inheritedEventType = 'VEHICLE_PURCHASE';
          break;
        }
        if (prev.includes('property') || prev.includes('house') || prev.includes('मकान') || prev.includes('जमीन')) {
          inheritedDomain = 'property';
          inheritedEventType = 'PROPERTY_PURCHASE';
          break;
        }
        if (prev.includes('change job') || prev.includes('job change') || prev.includes('switch job') || prev.includes('नौकरी बदल')) {
          inheritedDomain = 'career';
          inheritedEventType = 'JOB_CHANGE';
          break;
        }
        if (prev.includes('promotion') || prev.includes('प्रमोशन') || prev.includes('पदॊन्नति')) {
          inheritedDomain = 'career';
          inheritedEventType = 'PROMOTION';
          break;
        }
        if (prev.includes('career') || prev.includes('job') || prev.includes('work') || prev.includes('profession') || prev.includes('करियर') || prev.includes('नौकरी')) {
          inheritedDomain = 'career';
          inheritedEventType = 'CAREER_OVERVIEW';
          break;
        }
        if (prev.includes('business') || prev.includes('startup') || prev.includes('व्यापार')) {
          inheritedDomain = 'business';
          inheritedEventType = 'BUSINESS_STARTUP';
          break;
        }
        if (prev.includes('marriage') || prev.includes('spouse') || prev.includes('शादी') || prev.includes('विवाह')) {
          inheritedDomain = 'marriage';
          inheritedEventType = 'MARRIAGE_TIMING';
          break;
        }
        if (prev.includes('wealth') || prev.includes('money') || prev.includes('finance') || prev.includes('धन')) {
          inheritedDomain = 'wealth';
          inheritedEventType = 'WEALTH_OVERVIEW';
          break;
        }
        if (prev.includes('health') || prev.includes('disease') || prev.includes('स्वास्थ्य')) {
          inheritedDomain = 'health';
          inheritedEventType = 'HEALTH_OVERVIEW';
          break;
        }
      }
    }
  }

  // 3. Identify referenced year
  let referencedYear: number | null = null;
  const yearMatch = q.match(/\b(202[4-9]|203[0-9])\b/);
  if (yearMatch) {
    referencedYear = parseInt(yearMatch[1], 10);
  }

  // 4. Identify Timing Intent
  const isTimingRequested = !!referencedYear ||
    qLower.includes('when') ||
    qLower.includes('which year') ||
    qLower.includes('which period') ||
    qLower.includes('which month') ||
    qLower.includes('time frame') ||
    qLower.includes('timeframe') ||
    qLower.includes('timeline') ||
    qLower.includes('timing') ||
    qLower.includes('favorable period') ||
    qLower.includes('good time') ||
    qLower.includes('next job change') ||
    qLower.includes('kab') ||
    qLower.includes('कब') ||
    qLower.includes('किस वर्ष') ||
    qLower.includes('कब तक') ||
    qLower.includes('समय कैसा') ||
    qLower.includes('कब खरीद');

  // 5. Identify Question Type
  let questionType: QuestionType = 'PROMISE';
  if (isTimingRequested) {
    questionType = 'TIMING';
  } else if (
    qLower.includes('should i change jobs or start a business') ||
    qLower.includes('job vs business') ||
    qLower.includes('compare') ||
    qLower.includes('नौकरी या व्यापार') ||
    qLower.includes('तुलना')
  ) {
    questionType = 'COMPARISON';
  } else if (
    qLower.includes('should i') ||
    qLower.includes('can i') ||
    qLower.includes('better to') ||
    qLower.includes('leave my current') ||
    qLower.includes('resign') ||
    qLower.includes('क्या मुझे') ||
    qLower.includes('उचित रहेगा')
  ) {
    questionType = 'DECISION';
  } else if (
    qLower.includes('why is') ||
    qLower.includes('obstacle') ||
    qLower.includes('delay') ||
    qLower.includes('problem') ||
    qLower.includes('block') ||
    qLower.includes('बाधा') ||
    qLower.includes('समस्या')
  ) {
    questionType = 'OBSTACLE';
  } else if (
    qLower.includes('remedy') ||
    qLower.includes('remedies') ||
    qLower.includes('gemstone') ||
    qLower.includes('mantra') ||
    qLower.includes('upay') ||
    qLower.includes('उपाय') ||
    qLower.includes('रत्न')
  ) {
    questionType = 'REMEDY';
  } else if (
    qLower.includes('will i') ||
    qLower.includes('is there any chance') ||
    qLower.includes('probability') ||
    qLower.includes('क्या मुझे मिलेगी') ||
    qLower.includes('क्या संभावना')
  ) {
    questionType = 'PROMISE';
  } else if (
    qLower.includes('how is') ||
    qLower.includes('overview') ||
    qLower.includes('tell me about') ||
    qLower.includes('कैसा रहेगा')
  ) {
    questionType = 'GENERAL';
  }

  // 6. Event Identification (Specific Event Triggers)
  let eventType: EventType = 'GENERAL_OVERVIEW';
  let primaryDomain: PrimaryDomain = 'general';
  let secondaryDomain: PrimaryDomain | null = null;

  // Vehicle Purchase Events (HIGHEST PRIORITY when vehicle keywords present)
  if (hasExplicitVehicle) {
    eventType = 'VEHICLE_PURCHASE';
    primaryDomain = 'vehicle';
  }
  // Property & Real Estate Events
  else if (hasExplicitProperty) {
    eventType = 'PROPERTY_PURCHASE';
    primaryDomain = 'property';
  }
  // Career Events
  else if (
    qLower.includes('job change') ||
    qLower.includes('change job') ||
    qLower.includes('change my job') ||
    qLower.includes('switch job') ||
    qLower.includes('leave job') ||
    qLower.includes('leave current job') ||
    qLower.includes('next job change') ||
    qLower.includes('नौकरी बदल') ||
    qLower.includes('जॉब चेंज') ||
    (isFollowUp && inheritedEventType === 'JOB_CHANGE')
  ) {
    eventType = 'JOB_CHANGE';
    primaryDomain = 'career';
  } else if (
    qLower.includes('promotion') ||
    qLower.includes('appraisal') ||
    qLower.includes('promoted') ||
    qLower.includes('designation') ||
    qLower.includes('पदॊन्नति') ||
    qLower.includes('प्रमोशन')
  ) {
    eventType = 'PROMOTION';
    primaryDomain = 'career';
  } else if (
    qLower.includes('government job') ||
    qLower.includes('sarkari naukri') ||
    qLower.includes('civil services') ||
    qLower.includes('upsc') ||
    qLower.includes('psc') ||
    qLower.includes('सरकारी नौकरी')
  ) {
    eventType = 'GOVERNMENT_JOB';
    primaryDomain = 'career';
  } else if (
    qLower.includes('job vs business') ||
    qLower.includes('change jobs or start a business') ||
    qLower.includes('business or job') ||
    qLower.includes('नौकरी या व्यापार')
  ) {
    eventType = 'CAREER_PATH_COMPARISON';
    primaryDomain = 'career';
    secondaryDomain = 'business';
  } else if (
    qLower.includes('start a business') ||
    qLower.includes('start business') ||
    qLower.includes('own business') ||
    qLower.includes('startup') ||
    qLower.includes('venture') ||
    qLower.includes('नया व्यापार') ||
    qLower.includes('व्यवसाय शुरू')
  ) {
    eventType = 'BUSINESS_STARTUP';
    primaryDomain = 'business';
  } else if (
    qLower.includes('get a new job') ||
    qLower.includes('get new job') ||
    qLower.includes('first job') ||
    qLower.includes('employment') ||
    qLower.includes('नई नौकरी')
  ) {
    eventType = 'NEW_JOB';
    primaryDomain = 'career';
  } else if (
    qLower.includes('career') ||
    qLower.includes('job') ||
    qLower.includes('profession') ||
    qLower.includes('work') ||
    qLower.includes('करियर') ||
    qLower.includes('नौकरी')
  ) {
    eventType = 'CAREER_OVERVIEW';
    primaryDomain = 'career';
  }
  // Marriage Events
  else if (
    qLower.includes('marriage timing') ||
    qLower.includes('get married') ||
    qLower.includes('when will i marry') ||
    qLower.includes('wedding') ||
    qLower.includes('शादी कब') ||
    qLower.includes('विवाह कब')
  ) {
    eventType = 'MARRIAGE_TIMING';
    primaryDomain = 'marriage';
  } else if (hasExplicitMarriage) {
    eventType = 'MARRIAGE_OVERVIEW';
    primaryDomain = 'marriage';
  }
  // Foreign Travel
  else if (hasExplicitTravel) {
    eventType = 'FOREIGN_RELOCATION';
    primaryDomain = 'foreignTravel';
  }
  // Progeny vs Children's Education (Desh-Kaal-Patra Age-Aware)
  else if (
    qLower.includes('child birth') ||
    qLower.includes('baby') ||
    qLower.includes('conceive') ||
    qLower.includes('pregnancy') ||
    qLower.includes('progeny') ||
    qLower.includes('संतान') ||
    qLower.includes('बच्चे')
  ) {
    const isExplicitConception =
      qLower.includes('conceive') ||
      qLower.includes('pregnancy') ||
      qLower.includes('child birth') ||
      qLower.includes('गर्भाधान') ||
      qLower.includes('संतान प्राप्ति') ||
      qLower.includes('baby birth');

    if (nativeAge !== null && nativeAge >= 40 && !isExplicitConception) {
      // For mature natives, children inquiries naturally center on education, career and life progress
      eventType = 'CHILDREN_EDUCATION';
      primaryDomain = 'education';
    } else {
      eventType = 'CHILDBIRTH';
      primaryDomain = 'progeny';
    }
  }
  // Wealth & Finance
  else if (hasExplicitWealth) {
    eventType = isTimingRequested ? 'WEALTH_TIMING' : 'WEALTH_OVERVIEW';
    primaryDomain = 'wealth';
  }
  // Health
  else if (hasExplicitHealth) {
    eventType = isTimingRequested ? 'HEALTH_RECOVERY' : 'HEALTH_OVERVIEW';
    primaryDomain = 'health';
  }
  // Education
  else if (
    qLower.includes('exam') ||
    qLower.includes('education') ||
    qLower.includes('degree') ||
    qLower.includes('college') ||
    qLower.includes('पढ़ाई') ||
    qLower.includes('परीक्षा')
  ) {
    eventType = 'EDUCATION_EXAM';
    primaryDomain = 'education';
  }
  // Personality
  else if (
    qLower.includes('personality') ||
    qLower.includes('nature') ||
    qLower.includes('who am i') ||
    qLower.includes('व्यक्तित्व') ||
    qLower.includes('स्वभाव')
  ) {
    eventType = 'GENERAL_OVERVIEW';
    primaryDomain = 'personality';
  } else if (isFollowUp && inheritedDomain) {
    primaryDomain = inheritedDomain;
    if (inheritedEventType) eventType = inheritedEventType;
  }

  // 6. Identify referenced planet
  let referencedPlanet: string | null = null;
  const planetsList = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn', 'Rahu', 'Ketu'];
  for (const p of planetsList) {
    if (new RegExp(`\\b${p}\\b`, 'i').test(q)) {
      referencedPlanet = p;
      break;
    }
  }

  // 7. Identify referenced house
  let referencedHouse: number | null = null;
  const houseMatch = q.match(/\b([1-9]|1[0-2])(st|nd|rd|th)?\s*(house|bhava|भाव)\b/i) ||
    q.match(/\b(house|bhava|भाव)\s*([1-9]|1[0-2])\b/i);
  if (houseMatch) {
    referencedHouse = parseInt(houseMatch[1] || houseMatch[2], 10);
  }

  // 8. Methodology
  let requiresSpecializedMethod: 'PARASHARI' | 'JAIMINI' | 'KP' | 'DIVISIONAL_VARGA' | 'GENERAL' = 'PARASHARI';
  if (qLower.includes('kp') || qLower.includes('sub lord') || qLower.includes('sublord')) {
    requiresSpecializedMethod = 'KP';
  } else if (qLower.includes('jaimini') || qLower.includes('chara dasha') || qLower.includes('amatyakaraka')) {
    requiresSpecializedMethod = 'JAIMINI';
  } else if (qLower.includes('d10') || qLower.includes('d9') || qLower.includes('dashamsha') || qLower.includes('navamsha')) {
    requiresSpecializedMethod = 'DIVISIONAL_VARGA';
  }

  const isChildGated = nativeAge !== null && nativeAge < 11;

  return {
    primaryDomain,
    secondaryDomain,
    eventType,
    questionType,
    isTimingRequested,
    referencedYear,
    referencedPlanet,
    referencedHouse,
    referencedEvent: eventType,
    isFollowUp,
    inheritedDomain,
    inheritedEventType,
    isRemedyRequested: questionType === 'REMEDY',
    requiresSpecializedMethod,
    isChildGated,
    userQuery: q
  };
}

// ============================================================================
// 2. D10 DASHAMSHA & EVENT TIMING SYNTHESIS ENGINES
// ============================================================================

export interface D10AnalysisResult {
  d10LagnaSign: string;
  d10LagnaLord: string;
  d10TenthHouseSign: string;
  d10TenthLord: string;
  d10TenthLordPlacement: string;
  d10SixthLordPlacement: string;
  d10EleventhLordPlacement: string;
  exaltedPlanetsInD10: string[];
  debilitatedPlanetsInD10: string[];
  dashaLordD10Status: string;
  antardashaLordD10Status: string;
  refinementSummaryEn: string;
  refinementSummaryHi: string;
}

/**
 * Deep D10 Dashamsha Career Refinement Evaluator
 */
export function evaluateD10Dashamsha(
  kundali: CompleteKundali,
  currentMahadasha: string,
  currentAntardasha: string
): D10AnalysisResult {
  const d10Chart = kundali.charts?.['D10'] || kundali.charts?.['Dashamsha'];
  const planets = Array.isArray(kundali.planets) ? kundali.planets : [];
  const houses = Array.isArray(kundali.houses) ? kundali.houses : [];

  const defaultResult: D10AnalysisResult = {
    d10LagnaSign: 'Aries',
    d10LagnaLord: 'Mars',
    d10TenthHouseSign: 'Capricorn',
    d10TenthLord: 'Saturn',
    d10TenthLordPlacement: 'Stable career quadrant',
    d10SixthLordPlacement: 'Normal service endurance',
    d10EleventhLordPlacement: 'Gains through diligent execution',
    exaltedPlanetsInD10: [],
    debilitatedPlanetsInD10: [],
    dashaLordD10Status: `Dasha Lord ${currentMahadasha} has normal career support`,
    antardashaLordD10Status: `Antardasha Lord ${currentAntardasha} activates professional initiative`,
    refinementSummaryEn: `D10 confirms that career transitions yield structured growth and status stabilization rather than sudden upheaval.`,
    refinementSummaryHi: `दशमांश (D10) पुष्टि करता है कि कार्यक्षेत्र में परिवर्तन अचानक उथल-पुथल के बजाय सुनियोजित स्थायित्व लाएगा।`
  };

  if (!d10Chart || !Array.isArray(d10Chart.houses) || d10Chart.houses.length === 0) {
    return defaultResult;
  }

  const d10LagnaH = d10Chart.houses.find(h => h.houseNumber === 1) || d10Chart.houses[0];
  const d10LagnaSign = d10LagnaH.sign || 'Aries';
  const d10LagnaLord = d10LagnaH.signLord || getSignLord(d10LagnaSign);

  const d10TenthH = d10Chart.houses.find(h => h.houseNumber === 10) || d10Chart.houses[9] || { sign: 'Capricorn', signLord: 'Saturn' };
  const d10TenthSign = d10TenthH.sign || 'Capricorn';
  const d10TenthLord = d10TenthH.signLord || getSignLord(d10TenthSign);

  const d10SixthH = d10Chart.houses.find(h => h.houseNumber === 6) || { sign: 'Virgo', signLord: 'Mercury' };
  const d10SixthLord = d10SixthH.signLord || getSignLord(d10SixthH.sign);

  const d10EleventhH = d10Chart.houses.find(h => h.houseNumber === 11) || { sign: 'Aquarius', signLord: 'Saturn' };
  const d10EleventhLord = d10EleventhH.signLord || getSignLord(d10EleventhH.sign);

  const d10Planets: PlanetPosition[] = Array.isArray(d10Chart.planets) ? d10Chart.planets : [];

  const exalted = d10Planets.filter(p => p.dignity === 'Exalted').map(p => p.name);
  const deb = d10Planets.filter(p => p.dignity === 'Debilitated').map(p => p.name);

  const p10LordInD10 = d10Planets.find(p => p.name === d10TenthLord);
  const p6LordInD10 = d10Planets.find(p => p.name === d10SixthLord);
  const p11LordInD10 = d10Planets.find(p => p.name === d10EleventhLord);

  const pMdInD10 = d10Planets.find(p => p.name === currentMahadasha);
  const pAdInD10 = d10Planets.find(p => p.name === currentAntardasha);

  const dashaLordStatus = pMdInD10
    ? `Dasha Lord ${currentMahadasha} is placed in House ${pMdInD10.house} (${pMdInD10.sign}) [${pMdInD10.dignity}] in D10`
    : `Dasha Lord ${currentMahadasha} operates neutrally in D10`;

  const antardashaLordStatus = pAdInD10
    ? `Antardasha Lord ${currentAntardasha} sits in House ${pAdInD10.house} (${pAdInD10.sign}) [${pAdInD10.dignity}] in D10, activating ${pAdInD10.house === 10 || pAdInD10.house === 1 ? 'authority & leadership' : pAdInD10.house === 6 || pAdInD10.house === 11 ? 'job transition & new gains' : 'supportive career momentum'}`
    : `Antardasha Lord ${currentAntardasha} operates neutrally in D10`;

  let refinementEn = '';
  let refinementHi = '';

  if (p10LordInD10 && ['Exalted', 'Own House', 'Moolatrikona'].includes(p10LordInD10.dignity)) {
    refinementEn = `D10 Dashamsha exhibits high executive stature: 10th Lord ${d10TenthLord} is in ${p10LordInD10.dignity} dignity in House ${p10LordInD10.house}, confirming that any upcoming job change elevates role responsibility and professional authority.`;
    refinementHi = `दशमांश (D10) में दशमेश ${d10TenthLord} भाव ${p10LordInD10.house} में ${p10LordInD10.dignity} स्थिति में हैं, जो स्पष्ट करता है कि नौकरी में बदलाव प्रतिष्ठा व अधिकार क्षेत्र में वृद्धि लाएगा।`;
  } else if (p6LordInD10 && p11LordInD10 && [1, 6, 10, 11].includes(pAdInD10?.house || 0)) {
    refinementEn = `D10 Dashamsha shows strong service and gains coordination: Antardasha Lord ${currentAntardasha} is well-aligned with 6H/11H themes, ensuring favorable interview outcomes and beneficial compensation packages during transition.`;
    refinementHi = `दशमांश (D10) में अंतर्दशा स्वामी ${currentAntardasha} का 6ठे व 11वें भाव से शुभ संबंध है, जो साक्षात्कारों में सफलता और बेहतर पैकेज की पुष्टि करता है।`;
  } else {
    refinementEn = `D10 Dashamsha establishes solid foundational stamina with ${d10LagnaLord} guiding career direction, ensuring transition stability once initiated through planned steps.`;
    refinementHi = `दशमांश (D10) में लग्नेश ${d10LagnaLord} की स्थिति कार्यक्षेत्र में स्थिरता एवं सुनियोजित प्रगति प्रदान करती है।`;
  }

  return {
    d10LagnaSign,
    d10LagnaLord,
    d10TenthHouseSign: d10TenthSign,
    d10TenthLord,
    d10TenthLordPlacement: p10LordInD10 ? `House ${p10LordInD10.house} (${p10LordInD10.sign}) [${p10LordInD10.dignity}]` : 'Standard quadrant',
    d10SixthLordPlacement: p6LordInD10 ? `House ${p6LordInD10.house} (${p6LordInD10.sign})` : 'Service sector',
    d10EleventhLordPlacement: p11LordInD10 ? `House ${p11LordInD10.house} (${p11LordInD10.sign})` : 'Gains quadrant',
    exaltedPlanetsInD10: exalted,
    debilitatedPlanetsInD10: deb,
    dashaLordD10Status: dashaLordStatus,
    antardashaLordD10Status: antardashaLordStatus,
    refinementSummaryEn: refinementEn,
    refinementSummaryHi: refinementHi
  };
}

// ============================================================================
// 3. EVENT EVIDENCE MAP & TIMING ENGINE
// ============================================================================

export interface QuestionEvidencePackage {
  classification: QuestionClassification;
  nativeId: string;
  nativeAge: number | null;
  lagnaSign: string;
  lagnaLord: string;
  lagnaLordPlacement: PlanetPosition | undefined;
  moonSign: string;
  moonNakshatra: string;
  moonPada: number;
  currentMahadasha: string;
  currentAntardasha: string;
  antardashaStartDate: string;
  antardashaEndDate: string;
  upcomingAntardashas: Array<{ planet: string; startDate: string; endDate: string }>;
  primaryHouses: number[];
  primaryHousesLords: Array<{ house: number; sign: string; lord: string; planet?: PlanetPosition }>;
  occupantsInPrimaryHouses: Array<{ house: number; planetName: string; dignity: string }>;
  karakas: Array<{ name: string; planet?: PlanetPosition; significance: string }>;
  d10Analysis: D10AnalysisResult;
  relevantVargaChartName: string;
  relevantVargaFindings: string;
  shadbalaFactors: Array<{ planet: string; rupas: number; isStrong: boolean }>;
  savPoints: Array<{ house: number; points: number }>;
  yogasActive: string[];
  doshasActive: string[];
  gocharSaturn: { sign: string; house: number; isFavorable: boolean };
  gocharJupiter: { sign: string; house: number; isFavorable: boolean };
  doubleTransitActive: boolean;
  doubleTransitHouses: number[];
  eventTimingAnalysis: EventTimingAnalysis | null;
  evidenceItems: AstrologicalEvidenceItem[];
  dominantSupportingEvidence: AstrologicalEvidenceItem[];
  contradictoryEvidence: AstrologicalEvidenceItem[];
  qualifyingEvidence: AstrologicalEvidenceItem[];
  timingEvidence: AstrologicalEvidenceItem[];
  timingForecastSummary: string;
  balanceOfEvidenceVerdict: string;
}

/**
 * Builds the Question-First Astrological Evidence Package
 */
export function buildQuestionSpecificEvidence(
  classification: QuestionClassification,
  kundali: CompleteKundali,
  nativeAge: number | null = null
): QuestionEvidencePackage {
  const birthDetails = kundali.birthDetails || {};
  const planets: PlanetPosition[] = Array.isArray(kundali.planets) ? kundali.planets : [];
  const houses: HouseInfo[] = Array.isArray(kundali.houses) ? kundali.houses : [];
  const dashas: any[] = Array.isArray(kundali.dashas) ? kundali.dashas : [];
  const sav = kundali.ashtakvarga?.sarvashtakvarga || [28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28];

  const now = new Date();

  // Lagna & Moon
  const lagnaHouse = houses.find(h => h.houseNumber === 1) || houses[0] || { sign: 'Aries', houseNumber: 1, signLord: 'Mars' };
  const lagnaSign = lagnaHouse.sign || 'Aries';
  const lagnaLordName = lagnaHouse.signLord || getSignLord(lagnaSign);
  const lagnaLordPlacement = planets.find(p => p.name === lagnaLordName);

  const moonPlanet = planets.find(p => p.name === 'Moon');
  const moonSign = moonPlanet?.sign || 'Taurus';
  const moonNakshatra = kundali.panchang?.nakshatra || moonPlanet?.nakshatra || 'Rohini';
  const moonPada = moonPlanet?.pada || 1;

  // Dasha
  const currentDashaObj = dashas.find(d => d.isCurrent) || dashas[0] || {};
  const currentMahadasha = currentDashaObj.planet || 'Sun';
  const activeAntardashaObj =
    currentDashaObj.antardashas?.find((ad: any) => ad.isCurrent) ||
    currentDashaObj.antardashas?.find((ad: any) => {
      const start = new Date(ad.startDate).getTime();
      const end = new Date(ad.endDate).getTime();
      const curr = now.getTime();
      return curr >= start && curr <= end;
    }) || currentDashaObj.antardashas?.[0] || {};

  const currentAntardasha = activeAntardashaObj.planet || 'Moon';
  const antardashaStartDate = activeAntardashaObj.startDate || 'Current';
  const antardashaEndDate = activeAntardashaObj.endDate || 'Upcoming';

  const upcomingAntardashas: Array<{ planet: string; startDate: string; endDate: string }> = [];
  if (Array.isArray(currentDashaObj.antardashas)) {
    for (const ad of currentDashaObj.antardashas) {
      if (new Date(ad.endDate).getTime() >= now.getTime()) {
        upcomingAntardashas.push({
          planet: ad.planet,
          startDate: ad.startDate,
          endDate: ad.endDate
        });
      }
    }
  }

  // D10 Dashamsha Deep Analysis
  const d10Analysis = evaluateD10Dashamsha(kundali, currentMahadasha, currentAntardasha);

  // Shadbala
  const shadbala = kundali.shadbala || calculateShadbala(planets, houses, {
    birthDate: getBirthUtcDate(kundali.birthDetails),
    latitude: kundali.birthDetails.latitude,
    longitude: kundali.birthDetails.longitude
  });
  const shadbalaFactors = shadbala.planets.map(p => ({
    planet: p.planet,
    rupas: Number(p.totalRupas.toFixed(2)),
    isStrong: (p.ratio ?? (p.totalRupas / (p.requiredRupas || 6.0))) >= 1.0
  }));

  // Event-Driven Primary Houses & Karakas
  let primaryHouses: number[] = [10, 6, 11, 3, 12];
  let karakaNames: Array<{ name: string; significance: string }> = [
    { name: 'Sun', significance: 'Authority & Executive Stature' },
    { name: 'Saturn', significance: 'Karma, Service & Labor' },
    { name: 'Mercury', significance: 'Strategy, Commercial Agreements & Intellect' }
  ];
  let relevantVargaChartName = 'D10 Dashamsha';
  let relevantVargaKey = 'D10';

  switch (classification.eventType) {
    case 'JOB_CHANGE':
      // 10H (Status/Karma), 6H (Service/Daily Job), 11H (New Contract/Gains), 3H (Change/Initiative), 12H (Separation from current role)
      primaryHouses = [10, 6, 11, 3, 12];
      karakaNames = [
        { name: 'Saturn', significance: 'Job/Karma Significator' },
        { name: 'Mercury', significance: 'Negotiations & New Agreements' },
        { name: 'Sun', significance: 'Status & Authority' }
      ];
      relevantVargaChartName = 'D10 Dashamsha (Career Transition & Stature)';
      relevantVargaKey = 'D10';
      break;

    case 'PROMOTION':
      primaryHouses = [10, 11, 1, 6];
      karakaNames = [
        { name: 'Sun', significance: 'Elevation & Leadership' },
        { name: 'Jupiter', significance: 'Wisdom & Recognition' },
        { name: 'Saturn', significance: 'Service Maturation' }
      ];
      relevantVargaChartName = 'D10 Dashamsha (Promotion & Dignity)';
      relevantVargaKey = 'D10';
      break;

    case 'GOVERNMENT_JOB':
      primaryHouses = [10, 1, 6, 9];
      karakaNames = [
        { name: 'Sun', significance: 'Government Authority & Royal Favor' },
        { name: 'Mars', significance: 'Administrative Power & Competition' },
        { name: 'Jupiter', significance: 'Dharmic Service' }
      ];
      relevantVargaChartName = 'D10 Dashamsha (State Service)';
      relevantVargaKey = 'D10';
      break;

    case 'BUSINESS_STARTUP':
    case 'CAREER_PATH_COMPARISON':
      primaryHouses = [7, 10, 3, 11, 2];
      karakaNames = [
        { name: 'Mercury', significance: 'Commercial Acumen & Trade' },
        { name: 'Mars', significance: 'Risk Appetite & Enterprise' },
        { name: 'Jupiter', significance: 'Expansion & Capital' }
      ];
      relevantVargaChartName = 'D10 Dashamsha & D7 (Enterprise)';
      relevantVargaKey = 'D10';
      break;

    case 'MARRIAGE_TIMING':
    case 'MARRIAGE_OVERVIEW':
      primaryHouses = [7, 2, 11, 8];
      karakaNames = [
        { name: 'Venus', significance: 'Spouse & Harmonious Union' },
        { name: 'Jupiter', significance: 'Dharma & Family Grace' }
      ];
      relevantVargaChartName = 'D9 Navamsha (Marriage Covenant)';
      relevantVargaKey = 'D9';
      break;

    case 'VEHICLE_PURCHASE':
      primaryHouses = [4, 11, 2, 12];
      karakaNames = [
        { name: 'Venus', significance: 'Vahana Karaka (Conveyances, Luxury & Comfort)' },
        { name: 'Mars', significance: 'Machinery, Metal & Drive Propulsion' },
        { name: 'Moon', significance: 'Comfort & Travel Ease' }
      ];
      relevantVargaChartName = 'D4 Chaturthamsha & D16 Shodashamsha (Vehicles & Conveyances)';
      relevantVargaKey = 'D4';
      break;

    case 'PROPERTY_PURCHASE':
      primaryHouses = [4, 11, 12, 2];
      karakaNames = [
        { name: 'Mars', significance: 'Bhoomi Karaka (Land & Property)' },
        { name: 'Venus', significance: 'Luxury & Assets' },
        { name: 'Saturn', significance: 'Fixed Assets' }
      ];
      relevantVargaChartName = 'D4 Chaturthamsha (Real Estate & Fixed Assets)';
      relevantVargaKey = 'D4';
      break;

    case 'CHILDREN_EDUCATION':
      primaryHouses = [5, 9, 2, 4];
      karakaNames = [
        { name: 'Jupiter', significance: 'Guru & Wisdom' },
        { name: 'Mercury', significance: 'Intellect & Academic Skills' }
      ];
      relevantVargaChartName = 'D24 Siddhamsa (Higher Learning & Achievements)';
      relevantVargaKey = 'D24';
      break;

    case 'FOREIGN_RELOCATION':
      primaryHouses = [9, 12, 3, 7];
      karakaNames = [
        { name: 'Rahu', significance: 'Foreign Lands & Relocation' },
        { name: 'Moon', significance: 'Travel & Mobility' }
      ];
      relevantVargaChartName = 'D9 Navamsha & D4 (Overseas Fortune)';
      relevantVargaKey = 'D9';
      break;

    case 'CHILDBIRTH':
      primaryHouses = [5, 9, 2];
      karakaNames = [
        { name: 'Jupiter', significance: 'Putrakaraka (Children)' }
      ];
      relevantVargaChartName = 'D7 Saptamsha (Lineage & Progeny)';
      relevantVargaKey = 'D7';
      break;

    case 'WEALTH_TIMING':
    case 'WEALTH_OVERVIEW':
      primaryHouses = [2, 11, 5, 9];
      karakaNames = [
        { name: 'Jupiter', significance: 'Dhana Karaka (Prosperity)' },
        { name: 'Venus', significance: 'Assets & Reserves' },
        { name: 'Mercury', significance: 'Liquidity & Trade' }
      ];
      relevantVargaChartName = 'D2 Hora (Financial Accumulation)';
      relevantVargaKey = 'D2';
      break;

    case 'HEALTH_RECOVERY':
    case 'HEALTH_OVERVIEW':
      primaryHouses = [1, 6, 8, 12];
      karakaNames = [
        { name: 'Sun', significance: 'Physical Vitality & Immunity' },
        { name: 'Mars', significance: 'Cellular Repair & Surgery' },
        { name: 'Moon', significance: 'Emotional Health' }
      ];
      relevantVargaChartName = 'D3 Drekkana (Physical Constitution)';
      relevantVargaKey = 'D3';
      break;

    default:
      if (classification.primaryDomain === 'career') {
        primaryHouses = [10, 6, 11, 1];
        karakaNames = [
          { name: 'Sun', significance: 'Authority & Stature' },
          { name: 'Saturn', significance: 'Karma & Discipline' }
        ];
        relevantVargaChartName = 'D10 Dashamsha (Career)';
        relevantVargaKey = 'D10';
      } else {
        primaryHouses = [1, 5, 9, 10];
        karakaNames = [
          { name: 'Sun', significance: 'Core Self' },
          { name: 'Moon', significance: 'Mind' },
          { name: lagnaLordName, significance: 'Ascendant Ruler' }
        ];
        relevantVargaChartName = 'D9 Navamsha (Core Destiny)';
        relevantVargaKey = 'D9';
      }
      break;
  }

  // Primary House Lords & Occupants
  const primaryHousesLords = primaryHouses.map(hNum => {
    const hObj = houses.find(h => h.houseNumber === hNum) || { houseNumber: hNum, sign: 'Aries', signLord: 'Mars' };
    const lordName = hObj.signLord || getSignLord(hObj.sign);
    const pLord = planets.find(p => p.name === lordName);
    return {
      house: hNum,
      sign: hObj.sign,
      lord: lordName,
      planet: pLord
    };
  });

  const occupantsInPrimaryHouses: Array<{ house: number; planetName: string; dignity: string }> = [];
  for (const hNum of primaryHouses) {
    const occs = planets.filter(p => p.house === hNum);
    for (const occ of occs) {
      occupantsInPrimaryHouses.push({
        house: hNum,
        planetName: occ.name,
        dignity: occ.dignity
      });
    }
  }

  // Karakas
  const karakas = karakaNames.map(k => ({
    name: k.name,
    significance: k.significance,
    planet: planets.find(p => p.name === k.name)
  }));

  // SAV points
  const savPoints = primaryHouses.map(hNum => ({
    house: hNum,
    points: getSavByBhava(kundali.ashtakvarga, hNum, kundali.houses)
  }));

  // Gochar (Transits)
  const rawKundali = kundali as any;
  const gocharSaturn = {
    sign: rawKundali.transits?.saturn?.sign || 'Pisces',
    house: rawKundali.transits?.saturn?.house || 12,
    isFavorable: [3, 6, 11].includes(rawKundali.transits?.saturn?.house || 12)
  };

  const gocharJupiter = {
    sign: rawKundali.transits?.jupiter?.sign || 'Taurus',
    house: rawKundali.transits?.jupiter?.house || 2,
    isFavorable: [2, 5, 7, 9, 11].includes(rawKundali.transits?.jupiter?.house || 2)
  };

  // Double Transit Activation
  const doubleTransitHouses: number[] = [];
  for (const hNum of primaryHouses) {
    if ([10, 6, 11, 1, 7].includes(hNum)) {
      doubleTransitHouses.push(hNum);
    }
  }
  const doubleTransitActive = doubleTransitHouses.length > 0;

  // Relevant Varga Findings
  let relevantVargaFindings = d10Analysis.refinementSummaryEn;
  if (relevantVargaKey !== 'D10') {
    const vChart = kundali.charts?.[relevantVargaKey];
    if (vChart && Array.isArray(vChart.planets)) {
      const exaltedInVarga = vChart.planets.filter((p: any) => p.dignity === 'Exalted').map((p: any) => p.name);
      const ownInVarga = vChart.planets.filter((p: any) => p.dignity === 'Own House').map((p: any) => p.name);
      if (exaltedInVarga.length > 0 || ownInVarga.length > 0) {
        relevantVargaFindings = `Confirmed strong in ${relevantVargaKey}: ${[...exaltedInVarga.map(n => `${n} (Exalted)`), ...ownInVarga.map(n => `${n} (Own)`)].join(', ')}`;
      }
    }
  }

  // Active Yogas & Doshas
  const yogasActive: string[] = [];
  if (Array.isArray(rawKundali.yogas)) {
    for (const y of rawKundali.yogas) {
      if (typeof y === 'string') yogasActive.push(y);
      else if (y?.name) yogasActive.push(y.name);
    }
  }

  const doshasActive: string[] = [];
  if (kundali.manglik?.isManglik && ['marriage', 'relationship'].includes(classification.primaryDomain)) doshasActive.push('Mangal Dosha');
  if (kundali.kaalSarp?.hasKaalSarp && classification.primaryDomain === 'career') doshasActive.push('Kaal Sarp Dosha');
  if (kundali.sadeSati?.isInSadeSati) doshasActive.push(`Sade Sati (${kundali.sadeSati.phase})`);

  // ==========================================================================
  // EVENT-SPECIFIC CANDIDATE TIMING WINDOWS CALCULATION
  // ==========================================================================
  let eventTimingAnalysis: EventTimingAnalysis | null = null;

  if (classification.eventType === 'JOB_CHANGE' || classification.primaryDomain === 'career') {
    const tenthLord = primaryHousesLords.find(h => h.house === 10);
    const sixthLord = primaryHousesLords.find(h => h.house === 6);
    const eleventhLord = primaryHousesLords.find(h => h.house === 11);
    const thirdLord = primaryHousesLords.find(h => h.house === 3);
    const twelfthLord = primaryHousesLords.find(h => h.house === 12);

    const keyCareerLords = [
      tenthLord?.lord,
      sixthLord?.lord,
      eleventhLord?.lord,
      thirdLord?.lord,
      twelfthLord?.lord,
      'Mercury',
      'Saturn',
      'Sun'
    ].filter(Boolean) as string[];

    // Evaluate all upcoming antardashas for Job-Change activation score
    const scoredWindows: CandidateTimingWindow[] = [];

    for (const ad of upcomingAntardashas) {
      const adPlanetName = ad.planet;
      const adPlanetObj = planets.find(p => p.name === adPlanetName);
      let score = 50; // baseline
      let activationReasons: string[] = [];

      // 1. D1 House rulership or placement
      if (tenthLord?.lord === adPlanetName) {
        score += 25;
        activationReasons.push('10th Lord rulership (executive status)');
      }
      if (sixthLord?.lord === adPlanetName) {
        score += 20;
        activationReasons.push('6th Lord activation (service & competition)');
      }
      if (eleventhLord?.lord === adPlanetName) {
        score += 20;
        activationReasons.push('11th Lord activation (new contracts & gains)');
      }
      if (thirdLord?.lord === adPlanetName) {
        score += 15;
        activationReasons.push('3rd Lord activation (transition & initiatives)');
      }
      if (twelfthLord?.lord === adPlanetName) {
        score += 15;
        activationReasons.push('12th Lord separation from current role');
      }

      if (adPlanetObj && [10, 6, 11, 3, 1].includes(adPlanetObj.house)) {
        score += 15;
        activationReasons.push(`Placed in House ${adPlanetObj.house}`);
      }

      // 2. D10 Alignment
      const inD10 = d10Analysis.antardashaLordD10Status.includes(adPlanetName);
      if (inD10) {
        score += 15;
        activationReasons.push('Supportive D10 Dashamsha quadrant');
      }

      // 3. Dignity
      if (adPlanetObj && ['Exalted', 'Own House', 'Moolatrikona'].includes(adPlanetObj.dignity)) {
        score += 15;
        activationReasons.push(`Strong natal dignity (${adPlanetObj.dignity})`);
      } else if (adPlanetObj?.isCombust || adPlanetObj?.dignity === 'Debilitated') {
        score -= 10;
      }

      // Transit Support
      let transitDesc = 'Saturn provides steady grounding; Jupiter facilitates interview recognition.';
      if (gocharJupiter.isFavorable) {
        score += 10;
        transitDesc = `Jupiter transiting ${gocharJupiter.sign} provides benefic expansion and visibility.`;
      }

      scoredWindows.push({
        label: `${currentMahadasha}-${adPlanetName} Antardasha`,
        dateRange: `${ad.startDate} to ${ad.endDate}`,
        dashaLord: currentMahadasha,
        antardashaLord: adPlanetName,
        transitSupport: transitDesc,
        score,
        activationSummary: activationReasons.join(' + ') || 'Standard career progression'
      });
    }

    // Sort scored windows
    scoredWindows.sort((a, b) => b.score - a.score);

    const win1 = scoredWindows[0] || {
      label: `${currentMahadasha}-${currentAntardasha} Antardasha`,
      dateRange: `${antardashaStartDate} to ${antardashaEndDate}`,
      dashaLord: currentMahadasha,
      antardashaLord: currentAntardasha,
      transitSupport: 'Jupiter-Saturn transit alignment',
      score: 80,
      activationSummary: 'Active sub-period triggering career momentum'
    };

    const win2 = scoredWindows[1] || null;

    const natalPromise = tenthLord && tenthLord.planet
      ? `Strong natal foundation: 10th House (${tenthLord.sign}) is ruled by ${tenthLord.lord} in House ${tenthLord.planet.house} with ${tenthLord.planet.dignity} dignity, supported by 6th House (${sixthLord?.sign || 'Service'}) and 11th House (${eleventhLord?.sign || 'Gains'}).`
      : `Solid professional promise anchored by active 10H/6H/11H governance.`;

    const currentActivation = `Currently operating under ${currentMahadasha} Mahadasha and ${currentAntardasha} Antardasha (${antardashaStartDate} to ${antardashaEndDate}), directly engaging ${keyCareerLords.includes(currentAntardasha) ? 'primary career transition lords' : 'background career preparation'}.`;

    const delays: string[] = [];
    if (savPoints.find(s => s.house === 10)?.points! < 26) {
      delays.push('10th House SAV is below benchmark (28), requiring structured networking rather than hasty resignations.');
    }
    if (gocharSaturn.house === 8 || gocharSaturn.house === 12) {
      delays.push('Saturn transit in an introspective house indicates that initial negotiation rounds may take 4 to 6 weeks to finalize.');
    }
    if (delays.length === 0) {
      delays.push('No severe structural roadblocks detected; timely preparation and clear role-targeting are paramount.');
    }

    const practicalGuidance = [
      `Initiate resume distribution and active recruiter outreach 4 to 6 weeks ahead of the primary window (${win1.dateRange}).`,
      `Focus negotiations on strategic responsibility and long-term role architecture rather than quick lateral moves.`,
      `Avoid premature resignation before a formal written agreement is secured.`
    ];

    eventTimingAnalysis = {
      eventType: classification.eventType,
      eventName: 'Career / Job Transition',
      isTimingQuestion: classification.isTimingRequested,
      natalPromise,
      currentActivation,
      d10Refinement: d10Analysis.refinementSummaryEn,
      candidateWindow1: win1,
      candidateWindow2: win2,
      strongestWindow: win1.dateRange,
      secondaryWindow: win2 ? win2.dateRange : null,
      doubleTransitVerdict: doubleTransitActive
        ? 'Active Parashari Double Transit: Jupiter and Saturn simultaneous influence crystallizes job transition opportunities.'
        : 'Standard transit alignment supports strategic job movement.',
      delaysAndQualifications: delays,
      practicalGuidance,
      relevanceFilterApplied: true
    };
  } else if (classification.eventType === 'VEHICLE_PURCHASE') {
    const fourthLord = primaryHousesLords.find(h => h.house === 4);
    const eleventhLord = primaryHousesLords.find(h => h.house === 11);
    const secondLord = primaryHousesLords.find(h => h.house === 2);
    const venusPlanet = planets.find(p => p.name === 'Venus');
    const marsPlanet = planets.find(p => p.name === 'Mars');

    const keyVehicleLords = [
      fourthLord?.lord,
      'Venus',
      'Mars',
      eleventhLord?.lord,
      secondLord?.lord
    ].filter(Boolean) as string[];

    const scoredWindows: CandidateTimingWindow[] = [];

    for (const ad of upcomingAntardashas) {
      const adPlanetName = ad.planet;
      const adPlanetObj = planets.find(p => p.name === adPlanetName);
      let score = 50;
      let activationReasons: string[] = [];

      if (fourthLord?.lord === adPlanetName) {
        score += 30;
        activationReasons.push('4th Lord activation (Vahana Bhava ruler)');
      }
      if (adPlanetName === 'Venus') {
        score += 25;
        activationReasons.push('Venus activation (natural Karaka for vehicles & comforts)');
      }
      if (adPlanetName === 'Mars') {
        score += 20;
        activationReasons.push('Mars activation (machinery, engine & drive)');
      }
      if (eleventhLord?.lord === adPlanetName) {
        score += 20;
        activationReasons.push('11th Lord activation (fulfillment of desires & acquisition)');
      }
      if (secondLord?.lord === adPlanetName) {
        score += 15;
        activationReasons.push('2nd Lord activation (liquid reserves for asset acquisition)');
      }

      if (adPlanetObj && [4, 11, 2, 1, 9, 5].includes(adPlanetObj.house)) {
        score += 15;
        activationReasons.push(`Placed in House ${adPlanetObj.house}`);
      }

      if (adPlanetObj && ['Exalted', 'Own House', 'Moolatrikona'].includes(adPlanetObj.dignity)) {
        score += 15;
        activationReasons.push(`Strong dignity (${adPlanetObj.dignity})`);
      } else if (adPlanetObj?.isCombust || adPlanetObj?.dignity === 'Debilitated') {
        score -= 10;
      }

      let transitDesc = 'Benefic planetary transit supporting vehicle purchase and conveyance ease.';
      if (gocharJupiter.isFavorable) {
        score += 10;
        transitDesc = `Jupiter transiting ${gocharJupiter.sign} aspects 4th/11th house, bringing joyful asset acquisition.`;
      }

      scoredWindows.push({
        label: `${currentMahadasha}-${adPlanetName} Antardasha`,
        dateRange: `${ad.startDate} to ${ad.endDate}`,
        dashaLord: currentMahadasha,
        antardashaLord: adPlanetName,
        transitSupport: transitDesc,
        score,
        activationSummary: activationReasons.join(' + ') || 'Favorable conveyance period'
      });
    }

    scoredWindows.sort((a, b) => b.score - a.score);

    const win1 = scoredWindows[0] || {
      label: `${currentMahadasha}-${currentAntardasha} Antardasha`,
      dateRange: `${antardashaStartDate} to ${antardashaEndDate}`,
      dashaLord: currentMahadasha,
      antardashaLord: currentAntardasha,
      transitSupport: 'Supportive transit alignment for vehicle purchase',
      score: 85,
      activationSummary: 'Active sub-period triggering conveyance acquisition'
    };

    const win2 = scoredWindows[1] || null;

    const natalPromise = fourthLord && fourthLord.planet
      ? `Strong vehicle acquisition promise: 4th House (${fourthLord.sign}) is ruled by ${fourthLord.lord} in House ${fourthLord.planet.house} with ${fourthLord.planet.dignity} status, alongside Venus (${venusPlanet?.dignity || 'Functional'}) as Vahana Karaka.`
      : `Harmonious 4th House indicators support comfortable conveyance and vehicle purchase.`;

    const currentActivation = `Currently running ${currentMahadasha} Mahadasha and ${currentAntardasha} Antardasha (${antardashaStartDate} to ${antardashaEndDate}), directly engaging ${keyVehicleLords.includes(currentAntardasha) ? 'favorable vehicle acquisition lords' : 'preparatory financial accumulation'}.`;

    const delays: string[] = [];
    if (savPoints.find(s => s.house === 4)?.points! < 26) {
      delays.push('4th House SAV is modest, so ensure all vehicle inspection, warranty, and insurance terms are thoroughly verified.');
    }
    if (delays.length === 0) {
      delays.push('Planetary energies are supportive; align delivery with an auspicious weekday and nakshatra.');
    }

    const practicalGuidance = [
      `Take vehicle delivery on an auspicious day (such as Friday ruled by Venus or Thursday ruled by Jupiter) in a Shukla Paksha or Shubha Muhurta.`,
      `Perform a simple, traditional Vahana Puja (coconut, swastika, and floral offering) upon taking delivery to invite safety, longevity, and peaceful travels.`,
      `Verify vehicle registration numbers and choose auspicious numerological totals aligning with your favorable numbers.`
    ];

    eventTimingAnalysis = {
      eventType: classification.eventType,
      eventName: 'Vehicle / Automobile Purchase',
      isTimingQuestion: classification.isTimingRequested,
      natalPromise,
      currentActivation,
      d10Refinement: 'D4 Chaturthamsha confirms conveyance and asset comfort.',
      candidateWindow1: win1,
      candidateWindow2: win2,
      strongestWindow: win1.dateRange,
      secondaryWindow: win2 ? win2.dateRange : null,
      doubleTransitVerdict: 'Transit alignment directly empowers 4th house Vahana Sukha.',
      delaysAndQualifications: delays,
      practicalGuidance,
      relevanceFilterApplied: true
    };
  } else if (classification.eventType === 'PROPERTY_PURCHASE') {
    const fourthLord = primaryHousesLords.find(h => h.house === 4);
    const eleventhLord = primaryHousesLords.find(h => h.house === 11);
    const marsPlanet = planets.find(p => p.name === 'Mars');

    const keyPropertyLords = [
      fourthLord?.lord,
      'Mars',
      'Saturn',
      eleventhLord?.lord
    ].filter(Boolean) as string[];

    const scoredWindows: CandidateTimingWindow[] = [];

    for (const ad of upcomingAntardashas) {
      const adPlanetName = ad.planet;
      const adPlanetObj = planets.find(p => p.name === adPlanetName);
      let score = 50;
      let activationReasons: string[] = [];

      if (fourthLord?.lord === adPlanetName) {
        score += 30;
        activationReasons.push('4th Lord activation (Griha/Bhoomi ruler)');
      }
      if (adPlanetName === 'Mars') {
        score += 25;
        activationReasons.push('Mars activation (Bhoomi Karaka for fixed land/property)');
      }
      if (adPlanetName === 'Saturn') {
        score += 20;
        activationReasons.push('Saturn activation (long-term structural assets)');
      }
      if (eleventhLord?.lord === adPlanetName) {
        score += 20;
        activationReasons.push('11th Lord activation (gains and title acquisition)');
      }

      if (adPlanetObj && [4, 11, 2, 9].includes(adPlanetObj.house)) {
        score += 15;
        activationReasons.push(`Placed in House ${adPlanetObj.house}`);
      }

      scoredWindows.push({
        label: `${currentMahadasha}-${adPlanetName} Antardasha`,
        dateRange: `${ad.startDate} to ${ad.endDate}`,
        dashaLord: currentMahadasha,
        antardashaLord: adPlanetName,
        transitSupport: 'Planetary transit supporting fixed asset investment',
        score,
        activationSummary: activationReasons.join(' + ') || 'Property investment period'
      });
    }

    scoredWindows.sort((a, b) => b.score - a.score);

    const win1 = scoredWindows[0] || {
      label: `${currentMahadasha}-${currentAntardasha} Antardasha`,
      dateRange: `${antardashaStartDate} to ${antardashaEndDate}`,
      dashaLord: currentMahadasha,
      antardashaLord: currentAntardasha,
      transitSupport: 'Supportive transit for property',
      score: 80,
      activationSummary: 'Active sub-period triggering real estate movement'
    };

    const win2 = scoredWindows[1] || null;

    eventTimingAnalysis = {
      eventType: classification.eventType,
      eventName: 'Real Estate / Property Purchase',
      isTimingQuestion: classification.isTimingRequested,
      natalPromise: `4th House (${fourthLord?.sign || 'Assets'}) ruled by ${fourthLord?.lord || 'Lord'} and Mars as Bhoomi Karaka create solid potential for home ownership and real estate stability.`,
      currentActivation: `Operating under ${currentMahadasha} Mahadasha and ${currentAntardasha} Antardasha (${antardashaStartDate} to ${antardashaEndDate}).`,
      d10Refinement: 'D4 Chaturthamsha confirms real estate and fixed asset realization.',
      candidateWindow1: win1,
      candidateWindow2: win2,
      strongestWindow: win1.dateRange,
      secondaryWindow: win2 ? win2.dateRange : null,
      doubleTransitVerdict: 'Double transit energizes 4th house fixed asset accumulation.',
      delaysAndQualifications: ['Ensure thorough legal and registry verification prior to title transfer.'],
      practicalGuidance: [
        'Conduct detailed document and registry title checks during auspicious waxing Moon phases.',
        'Perform Griha Pravesh rituals aligned with auspicious Muhurta.'
      ],
      relevanceFilterApplied: true
    };
  }

  // Structured Evidence Items
  const evidenceItems: AstrologicalEvidenceItem[] = [];

  // Main House Lord
  const mainHouseNum = primaryHouses[0];
  const mainHouseLord = primaryHousesLords[0];
  if (mainHouseLord && mainHouseLord.planet) {
    const p = mainHouseLord.planet;
    const isStrong = ['Exalted', 'Own House', 'Moolatrikona'].includes(p.dignity);
    const isAfflicted = ['Debilitated', 'Great Enemy'].includes(p.dignity) || p.isCombust;
    evidenceItems.push({
      id: `HL_${mainHouseNum}_DIGNITY`,
      factorName: `${formatOrdinal(mainHouseNum)} Lord (${p.name}) Status`,
      category: 'BHAVA_LORDSHIP',
      source: `D1 ${formatOrdinal(mainHouseNum)} House (${mainHouseLord.sign})`,
      weight: 9,
      classification: isStrong ? 'SUPPORTING' : isAfflicted ? 'CONTRADICTING' : 'QUALIFYING',
      summaryEn: `${formatOrdinal(mainHouseNum)} Lord ${p.name} is in ${p.dignity} status in House ${p.house}.`,
      summaryHi: `${mainHouseNum}वें भाव के स्वामी ${p.name} भाव ${p.house} में ${p.dignity} स्थिति में हैं।`,
      astrologicalDetail: `Foundational governor for ${classification.eventType} outcomes.`
    });
  }

  // Main Karaka
  if (karakas.length > 0 && karakas[0].planet) {
    const kPlanet = karakas[0].planet;
    const isKStrong = ['Exalted', 'Own House', 'Moolatrikona'].includes(kPlanet.dignity);
    const isKAfflicted = ['Debilitated', 'Great Enemy'].includes(kPlanet.dignity) || kPlanet.isCombust;
    evidenceItems.push({
      id: `KR_${kPlanet.name}_DIGNITY`,
      factorName: `Karaka ${kPlanet.name} (${karakas[0].significance})`,
      category: 'CHARA_KARAKA',
      source: `D1 ${kPlanet.name} in House ${kPlanet.house}`,
      weight: 8,
      classification: isKStrong ? 'SUPPORTING' : isKAfflicted ? 'CONTRADICTING' : 'QUALIFYING',
      summaryEn: `Primary Karaka ${kPlanet.name} holds ${kPlanet.dignity} dignity in House ${kPlanet.house}.`,
      summaryHi: `मुख्य कारक ${kPlanet.name} भाव ${kPlanet.house} में ${kPlanet.dignity} स्थिति में हैं।`,
      astrologicalDetail: `Natural significator for ${classification.eventType}.`
    });
  }

  // D10 Refinement Evidence Item
  if (classification.primaryDomain === 'career') {
    evidenceItems.push({
      id: 'D10_DASHAMSHA_ALIGNMENT',
      factorName: 'D10 Dashamsha Career Refinement',
      category: 'DIVISIONAL_VARGA',
      source: 'D10 Dashamsha',
      weight: 9,
      classification: 'SUPPORTING',
      summaryEn: d10Analysis.refinementSummaryEn,
      summaryHi: d10Analysis.refinementSummaryHi,
      astrologicalDetail: `D10 Lagna Lord: ${d10Analysis.d10LagnaLord}, 10th Lord: ${d10Analysis.d10TenthLord} (${d10Analysis.d10TenthLordPlacement}).`
    });
  }

  // Active Dasha Timing Item
  evidenceItems.push({
    id: 'VIMSHOTTARI_DASHA_ACTIVATION',
    factorName: `Mahadasha (${currentMahadasha}) & Antardasha (${currentAntardasha})`,
    category: 'VIMSHOTTARI_DASHA',
    source: `Active Period (${antardashaStartDate} to ${antardashaEndDate})`,
    weight: 9,
    classification: 'TIMING_ACTIVATING',
    summaryEn: eventTimingAnalysis ? `Strongest Window: ${eventTimingAnalysis.strongestWindow} (${eventTimingAnalysis.candidateWindow1?.label})` : `Running ${currentMahadasha}-${currentAntardasha} Antardasha until ${antardashaEndDate}.`,
    summaryHi: eventTimingAnalysis ? `सर्वाधिक अनुकूल समय: ${eventTimingAnalysis.strongestWindow}` : `वर्तमान में ${currentMahadasha}-${currentAntardasha} का समय सक्रिय है।`,
    astrologicalDetail: `Parashari Vimshottari timing gateway.`
  });

  const dominantSupportingEvidence = evidenceItems.filter(e => e.classification === 'SUPPORTING' && e.weight >= 8);
  const contradictoryEvidence = evidenceItems.filter(e => e.classification === 'CONTRADICTING');
  const qualifyingEvidence = evidenceItems.filter(e => e.classification === 'QUALIFYING');
  const timingEvidence = evidenceItems.filter(e => e.classification === 'TIMING_ACTIVATING' || e.classification === 'TIMING_INACTIVE');

  const timingForecastSummary = eventTimingAnalysis
    ? `Strongest transition window: ${eventTimingAnalysis.strongestWindow} (${eventTimingAnalysis.candidateWindow1?.label})${eventTimingAnalysis.secondaryWindow ? `; Secondary window: ${eventTimingAnalysis.secondaryWindow}` : ''}.`
    : `Running ${currentMahadasha} Mahadasha with ${currentAntardasha} Antardasha until ${antardashaEndDate}.`;

  const balanceOfEvidenceVerdict = eventTimingAnalysis
    ? `Strong astrological promise for ${eventTimingAnalysis.eventName}, with optimal activation during ${eventTimingAnalysis.strongestWindow}.`
    : `Clear baseline potential supported by ${dominantSupportingEvidence.map(e => e.factorName).join(', ') || 'natal planetary configuration'}.`;

  return {
    classification,
    nativeId: kundali.birthDetails?.name || 'Native',
    nativeAge,
    lagnaSign,
    lagnaLord: lagnaLordName,
    lagnaLordPlacement,
    moonSign,
    moonNakshatra,
    moonPada,
    currentMahadasha,
    currentAntardasha,
    antardashaStartDate,
    antardashaEndDate,
    upcomingAntardashas,
    primaryHouses,
    primaryHousesLords,
    occupantsInPrimaryHouses,
    karakas,
    d10Analysis,
    relevantVargaChartName,
    relevantVargaFindings,
    shadbalaFactors,
    savPoints,
    yogasActive,
    doshasActive,
    gocharSaturn,
    gocharJupiter,
    doubleTransitActive,
    doubleTransitHouses,
    eventTimingAnalysis,
    evidenceItems,
    dominantSupportingEvidence,
    contradictoryEvidence,
    qualifyingEvidence,
    timingEvidence,
    timingForecastSummary,
    balanceOfEvidenceVerdict
  };
}

// ============================================================================
// 4. CONSULTATION PROMPT & CONTEXT ASSEMBLER (FOR GEMINI / LLM)
// ============================================================================

export function assembleAstrologerConsultationContext(
  evidencePkg: QuestionEvidencePackage,
  userRole: string = 'user'
): string {
  const {
    classification,
    nativeId,
    nativeAge,
    lagnaSign,
    lagnaLord,
    lagnaLordPlacement,
    moonSign,
    moonNakshatra,
    moonPada,
    currentMahadasha,
    currentAntardasha,
    antardashaStartDate,
    antardashaEndDate,
    upcomingAntardashas,
    primaryHousesLords,
    occupantsInPrimaryHouses,
    karakas,
    d10Analysis,
    relevantVargaChartName,
    shadbalaFactors,
    savPoints,
    yogasActive,
    doshasActive,
    gocharSaturn,
    gocharJupiter,
    doubleTransitActive,
    eventTimingAnalysis,
    dominantSupportingEvidence,
    contradictoryEvidence,
    qualifyingEvidence,
    timingForecastSummary,
    balanceOfEvidenceVerdict
  } = evidencePkg;

  const isJobChangeQuestion = classification.eventType === 'JOB_CHANGE' || (classification.primaryDomain === 'career' && classification.isTimingRequested);

  // Build domain-specific varga and rule context
  let vargaContextStr = '';
  if (classification.primaryDomain === 'career' || classification.eventType === 'JOB_CHANGE') {
    vargaContextStr = `- D10 Dashamsha Career Matrix: D10 Lagna ${d10Analysis.d10LagnaSign} (Lord ${d10Analysis.d10LagnaLord}), D10 10th Lord ${d10Analysis.d10TenthLord} (${d10Analysis.d10TenthLordPlacement}), D10 6th Lord (${d10Analysis.d10SixthLordPlacement}), D10 11th Lord (${d10Analysis.d10EleventhLordPlacement}).
- D10 Refinement Finding: ${d10Analysis.refinementSummaryEn}`;
  } else if (classification.primaryDomain === 'vehicle') {
    vargaContextStr = `- D16 Shodashamsha (Conveyance & Vehicle Comfort): 4th House Vahana Sukha, Venus (Vahana Karaka), Mars (mechanical drive) and D16 alignment indicate high comfort, protection, and auspicious acquisition timing.
- Vehicle Assessment: ${evidencePkg.relevantVargaFindings}`;
  } else if (classification.primaryDomain === 'property') {
    vargaContextStr = `- D4 Chaturthamsha (Real Estate & Fixed Assets): 4th House (Bhoomi/Griha), Mars (Bhoomi Karaka), Saturn, and D4 alignment support stable property acquisition and equity growth.
- Property Assessment: ${evidencePkg.relevantVargaFindings}`;
  } else if (classification.primaryDomain === 'marriage') {
    vargaContextStr = `- D9 Navamsha (Dharma & Partnership Matrix): 7th House, Venus/Jupiter, Dara Karaka, and D9 Navamsha balance indicate relationship harmony and partner compatibility.
- Relationship Assessment: ${evidencePkg.relevantVargaFindings}`;
  } else if (classification.primaryDomain === 'progeny') {
    vargaContextStr = `- D7 Saptamsha (Progeny & Lineage): 5th House, Jupiter (Putrakaraka), and D7 Saptamsha indicators support children's vitality and intellectual growth.
- Progeny Assessment: ${evidencePkg.relevantVargaFindings}`;
  } else if (classification.primaryDomain === 'education') {
    vargaContextStr = `- D24 Siddhamsa / Chaturvimshamsha (Higher Learning & Intellect): 4th/5th/9th houses, Mercury, Jupiter Vidyakaraka support academic excellence.
- Education Assessment: ${evidencePkg.relevantVargaFindings}`;
  } else {
    vargaContextStr = `- Divisional Matrix (${evidencePkg.relevantVargaChartName}): ${evidencePkg.relevantVargaFindings}`;
  }

  return `
=== VERIFIED ASTROLOGICAL EVIDENCE MAP ===
- Native Name: ${nativeId}
- Current Age: ${nativeAge !== null ? `${nativeAge} years old` : 'Adult'} ${classification.isChildGated ? '[CHILD AGE STAGE GATING ACTIVE: Under 11 years - Focus strictly on cognitive curiosity, learning habits and parental support. Do NOT predict career/marriage.]' : ''}
- Lagna: ${lagnaSign} (Lagna Lord: ${lagnaLord}${lagnaLordPlacement ? ` in House ${lagnaLordPlacement.house} [${lagnaLordPlacement.dignity}]` : ''})
- Moon: ${moonSign} (Nakshatra: ${moonNakshatra} Pada ${moonPada})

=== QUESTION & EVENT CLASSIFICATION ===
- User Question: "${classification.userQuery}"
- Classified Event: ${classification.eventType} (${classification.primaryDomain.toUpperCase()})
- Question Nature: ${classification.questionType} ${classification.isTimingRequested ? '[TIMING WINDOWS REQUIRED]' : ''} ${classification.referencedYear ? `[Target Year: ${classification.referencedYear}]` : ''}

=== EVENT-SPECIFIC NATAL & DIVISIONAL EVIDENCE ===
- Primary Event Houses: ${primaryHousesLords.map(hl => `${hl.house}H (${hl.sign}) Lord: ${hl.lord}${hl.planet ? ` [House ${hl.planet.house}, ${hl.planet.dignity}]` : ''}`).join(' | ')}
- Occupants in Event Houses: ${occupantsInPrimaryHouses.length > 0 ? occupantsInPrimaryHouses.map(o => `${o.planetName} (${o.dignity}) in ${o.house}H`).join(', ') : 'None (Empty Houses)'}
- Relevant Karakas: ${karakas.map(k => `${k.name} (${k.significance})${k.planet ? ` in ${k.planet.sign} [House ${k.planet.house}, ${k.planet.dignity}]` : ''}`).join(', ')}
${vargaContextStr}
- Relevant House SAV Points: ${savPoints.map(s => `${s.house}H: ${s.points} pts`).join(', ')} (Benchmark: 28)
- Key Planet Shadbala: ${shadbalaFactors.slice(0, 3).map(s => `${s.planet} (${s.rupas} Rupas: ${s.isStrong ? 'Strong' : 'Moderate'})`).join(', ')}

=== DASHA & TRANSIT TIMING CALCULATIONS ===
- Active Vimshottari Mahadasha: ${currentMahadasha}
- Active Antardasha: ${currentAntardasha} (${antardashaStartDate} to ${antardashaEndDate})
- Upcoming Antardashas Timeline: ${upcomingAntardashas.map(ad => `${ad.planet}: ${ad.startDate} to ${ad.endDate}`).join(', ')}
- Transits: Saturn in ${gocharSaturn.sign} (House ${gocharSaturn.house} from Lagna); Jupiter in ${gocharJupiter.sign} (House ${gocharJupiter.house} from Lagna)
- Double Transit Status: ${doubleTransitActive ? 'Active Double Transit simultaneously activating primary event houses.' : 'Standard transit currents.'}

${eventTimingAnalysis ? `
=== CALCULATED TIMING WINDOWS (DO NOT INVENT DATES) ===
- Strongest Supported Window: ${eventTimingAnalysis.strongestWindow} (${eventTimingAnalysis.candidateWindow1?.label}) -> Evidence: ${eventTimingAnalysis.candidateWindow1?.activationSummary}
${eventTimingAnalysis.secondaryWindow ? `- Secondary Supported Window: ${eventTimingAnalysis.secondaryWindow} (${eventTimingAnalysis.candidateWindow2?.label}) -> Evidence: ${eventTimingAnalysis.candidateWindow2?.activationSummary}` : ''}
- Delays & Astrological Qualifications: ${eventTimingAnalysis.delaysAndQualifications.join('; ')}
- Practical Strategic Guidance: ${eventTimingAnalysis.practicalGuidance.join('; ')}
` : ''}

=== STRICT RELEVANCE FILTER DIRECTIVE ===
${isJobChangeQuestion ? `
CRITICAL: The user is specifically asking about a Job Change / Career Event.
1. DO NOT dump generic personality traits ("You are resilient...", "You are disciplined...").
2. DO NOT dump unrelated Lagna / Moon essays or generic Mangal Dosha / Pitra Dosha / spirituality details.
3. DO NOT call an entire multi-year Mahadasha a "forecast window".
4. DIRECTLY ANSWER the timing question first using the calculated Strongest Window (${eventTimingAnalysis?.strongestWindow || antardashaEndDate}).
5. Explain WHY using 10H/10L + 6H/11H + 3H/12H + D10 Dashamsha + Dasha/Antardasha + Jupiter/Saturn Double Transit.
6. Provide concrete practical guidance for the transition period.
` : classification.primaryDomain === 'vehicle' ? `
CRITICAL: The user is specifically asking about a Vehicle / Conveyance (कार / वाहन).
1. Answer directly regarding vehicle purchase timing, conveyance happiness (Vahana Sukha), 4th house, Venus (Vahana Karaka), Mars, and D16.
2. DO NOT discuss career, job changes, promotion, or D10 Dashamsha!
3. Provide practical, conversational advice (auspicious days, colors, testing).
` : classification.primaryDomain === 'property' ? `
CRITICAL: The user is specifically asking about Property / Real Estate / Land (भूमि / मकान).
1. Answer directly regarding property acquisition, 4th house (Bhoomi), Mars, Saturn, and D4.
2. DO NOT discuss unrelated topics like career job changes or marriage!
` : `
Provide a focused consultation answering the user's specific query without generic filler or unrelated chart dumps.
`}
`;
}

// ============================================================================
// 5. CHART-GROUNDED OFFLINE / FALLBACK CONSULTATION ENGINE
// ============================================================================

export function generateChartGroundedConsultation(
  classification: QuestionClassification,
  evidencePkg: QuestionEvidencePackage,
  lang: Language = 'en'
): string {
  const isHi = lang === 'hi';
  const {
    nativeId,
    nativeAge,
    lagnaSign,
    lagnaLord,
    lagnaLordPlacement,
    currentMahadasha,
    currentAntardasha,
    antardashaStartDate,
    antardashaEndDate,
    primaryHousesLords,
    karakas,
    d10Analysis,
    savPoints,
    gocharSaturn,
    gocharJupiter,
    doubleTransitActive,
    eventTimingAnalysis
  } = evidencePkg;

  const now = new Date();
  const currentMonthYearStr = now.toLocaleString(isHi ? 'hi-IN' : 'en-US', { month: 'long', year: 'numeric' });

  // 1. CHILD GATING
  if (classification.isChildGated) {
    if (isHi) {
      return `🕉️ **ज्योतिषीय परामर्श — ${nativeId} (${nativeAge} वर्ष)** — *${currentMonthYearStr}*

1. **मार्गदर्शन (देश-काल-पात्र)**: जातक अभी बाल्यावस्था (${nativeAge} वर्ष) में है। वैदिक ज्योतिष के शास्त्रीय सिद्धांतों के अनुसार इस आयु में करियर या आजीविका संबंधी भविष्यवाणियां करना अनुचित है।
2. **संज्ञानात्मक विकास**: लग्न **${lagnaSign}** और बुद्धिकारक स्थिति बताती है कि जातक अवधारणात्मक (कन्सेप्चुअल) रूप से खेल व रचनात्मक गतिविधियों से तीव्रता से सीखता है।
3. **सक्रिय समय**: वर्तमान में **${currentMahadasha}-${currentAntardasha}** का समय शारीरिक ऊर्जा और एकाग्रता के निर्माण के लिए अनुकूल है।
4. **अभिभावकों के लिए सलाह**: किसी तय करियर का दबाव न बनाएं; स्वस्थ अध्ययन वातावरण और रचनात्मक प्रोत्साहन प्रदान करें।`;
    }

    return `🕉️ **Vedic Consultation for ${nativeId} (${nativeAge} yrs)** — *${currentMonthYearStr}*

1. **Developmental Stage (Desh-Kaal-Patra)**: The native is in their formative childhood years (${nativeAge} years old). Under authentic Parashari principles, definitive adult career forecasts are premature and non-applicable.
2. **Cognitive Learning Style**: **${lagnaSign}** Lagna ruled by **${lagnaLord}** with Mercury indicators shows a natural conceptual grasp that thrives with experiential encouragement rather than rote exam stress.
3. **Active Period Support**: Running **${currentMahadasha}-${currentAntardasha}** fosters healthy curiosity, cognitive habits, and vitality.
4. **Parental Guidance**: Foster open curiosity and consistent daily study habits without imposing early career expectations.`;
  }

  // 2. JOB CHANGE / CAREER EVENT CONSULTATION (PRECISE STRUCTURED RESPONSE)
  if (classification.eventType === 'JOB_CHANGE' || (classification.primaryDomain === 'career' && classification.isTimingRequested)) {
    const win1 = eventTimingAnalysis?.candidateWindow1;
    const win2 = eventTimingAnalysis?.candidateWindow2;
    const tenthLord = primaryHousesLords.find(h => h.house === 10);
    const sixthLord = primaryHousesLords.find(h => h.house === 6);
    const eleventhLord = primaryHousesLords.find(h => h.house === 11);
    const thirdLord = primaryHousesLords.find(h => h.house === 3);

    if (isHi) {
      return `🕉️ **कार्यक्षेत्र परामर्श: नौकरी परिवर्तन एवं समय चक्र (${nativeId})** — *${currentMonthYearStr}*

### 1. प्रश्न का सीधा उत्तर (Direct Answer & Timing)
आपकी कुंडली में नौकरी परिवर्तन (Job Change) का **सर्वाधिक प्रबल समय ${win1 ? win1.dateRange : antardashaEndDate} (${win1 ? win1.label : currentAntardasha + ' अंतर्दशा'})** के दौरान निर्मित हो रहा है${win2 ? `। इसके अतिरिक्त एक द्वितीयक अवसर **${win2.dateRange} (${win2.label})** के आसपास प्राप्त होगा।` : '।'}

### 2. कुंडली का शास्त्रीय आधार (Natal Career Promise)
- **दशम भाव (कर्म व पद प्रतिष्ठा)**: दशमेश **${tenthLord?.lord || 'दशमेश'}** ${tenthLord?.planet ? `भाव ${tenthLord.planet.house} में ${tenthLord.planet.dignity}` : ''} स्थित हैं, जो पेशेवर स्थिरता का आधार बनाते हैं।
- **छठा एवं ग्यारहवां भाव (सेवा एवं नवीन अनुबंध)**: षष्ठेश **${sixthLord?.lord}** तथा एकादशेश **${eleventhLord?.lord}** नए अनुबंधों और साक्षात्कार में सफलता का मार्ग प्रशस्त करते हैं।
- **तीसरा भाव (परिवर्तन व नई पहल)**: तृतीयेश **${thirdLord?.lord}** वर्तमान पद से आगे बढ़कर नई जिम्मेदारियां लेने की ऊर्जा सक्रिय करते हैं।

### 3. दशमांश (D10 Dashamsha) सूक्ष्म विश्लेषण
- ${d10Analysis.refinementSummaryHi}
- दशमांश लग्न **${d10Analysis.d10LagnaSign}** (स्वामी: **${d10Analysis.d10LagnaLord}**) तथा दशमेश **${d10Analysis.d10TenthLord}** की स्थिति यह दर्शाती है कि यह बदलाव केवल पद परिवर्तन नहीं बल्कि कौशल वृद्धि व दीर्घकालिक लाभ का माध्यम बनेगा।

### 4. दशा एवं गोचर समय चक्र (Timing Alignment)
- **विंशोत्तरी दशा**: वर्तमान **${currentMahadasha}** महादशा में **${currentAntardasha}** अंतर्दशा (${antardashaStartDate} से ${antardashaEndDate}) सीधे कार्यक्षेत्र के भावों को गति दे रही है।
- **गुरु-शनि गोचर प्रभाव**: गुरु ${gocharJupiter.sign} में एवं शनि ${gocharSaturn.sign} में गोचरस्थ हैं। ${doubleTransitActive ? 'पारशरी डबल गोचर दशम/एकादश भाव को सक्रिय कर रहा है।' : 'गोचर अनुकूलता निर्णयों में सहायता कर रही है।'}

### 5. सावधानियां एवं सीमाएं (Qualifications)
- ${eventTimingAnalysis?.delaysAndQualifications.join('\n- ') || 'भावनात्मक जल्दबाजी में वर्तमान पद से त्यागपत्र न दें; जब तक विधिवत प्रस्ताव पत्र (Offer Letter) न मिले, गोपनीयता बनाए रखें।'}

### 6. व्यावहारिक रणनीति (Practical Action)
- ${eventTimingAnalysis?.practicalGuidance.join('\n- ') || 'लक्षित समय से 4-6 सप्ताह पूर्व अपने लिंक्डइन व बायोडाटा को अद्यतन करें तथा वरिष्ठ संपर्कों से संवाद स्थापित करें।'}`;
    }

    // English Consultation
    return `🕉️ **Vedic Career Consultation: Job Transition Analysis for ${nativeId}** — *${currentMonthYearStr}*

### 1. Direct Consultation Answer (Timing Windows)
Your chart indicates the **strongest window for a job transition between ${win1 ? win1.dateRange : antardashaEndDate} (during ${win1 ? win1.label : currentAntardasha + ' Antardasha'})**${win2 ? `, with a secondary window emerging around **${win2.dateRange} (${win2.label})**.` : '.'}

### 2. Astrological Foundation (Natal Promise)
- **10th House & Karma Governance**: 10th Lord **${tenthLord?.lord || 'Lord'}** ${tenthLord?.planet ? `is placed in House ${tenthLord.planet.house} with ${tenthLord.planet.dignity} dignity` : ''}, anchoring your core professional reputation.
- **6th & 11th House Matrix (Service & Gains)**: 6th Lord **${sixthLord?.lord}** (competitive edge/interviews) and 11th Lord **${eleventhLord?.lord}** (offers & salary gains) activate favorable negotiation terms.
- **3rd House (Initiative & Movement)**: 3rd Lord **${thirdLord?.lord}** governs the transition dynamics needed to leave your existing routine and secure new responsibilities.

### 3. D10 Dashamsha Refinement
- ${d10Analysis.refinementSummaryEn}
- D10 Lagna is in **${d10Analysis.d10LagnaSign}** (Lord: **${d10Analysis.d10LagnaLord}**) with 10th Lord **${d10Analysis.d10TenthLord}** placed in **${d10Analysis.d10TenthLordPlacement}**, confirming that this transition supports structural career advancement rather than unstable lateral shifting.

### 4. Dasha & Transit Mechanics (Timing Synthesis)
- **Vimshottari Dasha**: Active **${currentMahadasha}** Mahadasha and **${currentAntardasha}** Antardasha (${antardashaStartDate} to ${antardashaEndDate}) engage the relevant transition lords.
- **Transits & Double Transit**: Saturn transiting in ${gocharSaturn.sign} and Jupiter in ${gocharJupiter.sign} ${doubleTransitActive ? 'create an active Double Transit on your primary career houses, crystallizing new employment avenues.' : 'provide stable transit currents for professional restructuring.'}

### 5. Qualifications & Potential Delays
- ${eventTimingAnalysis?.delaysAndQualifications.join('\n- ') || 'Do not tender a resignation on impulse; initial salary negotiations may require 3-4 weeks to reach your target band.'}

### 6. Practical Strategic Guidance
- ${eventTimingAnalysis?.practicalGuidance.join('\n- ') || 'Initiate recruiter outreach 4 to 6 weeks ahead of the primary window, and verify all contract terms prior to transition.'}`;
  }

  // 3. VEHICLE PURCHASE CONSULTATION
  if (classification.primaryDomain === 'vehicle' || classification.eventType === 'VEHICLE_PURCHASE') {
    const fourthLord = primaryHousesLords.find(h => h.house === 4);
    const win1 = eventTimingAnalysis?.candidateWindow1;
    const win2 = eventTimingAnalysis?.candidateWindow2;

    if (isHi) {
      return `🕉️ **वाहन सुख एवं क्रय परामर्श (${nativeId})** — *${currentMonthYearStr}*

### 1. वाहन क्रय का सबसे अनुकूल समय
आपकी कुंडली में वाहन क्रय (कार/सवारी) का **सर्वाधिक अनुकूल व शुभ समय ${win1 ? win1.dateRange : antardashaEndDate} (${win1 ? win1.label : currentAntardasha + ' अंतर्दशा'})** के मध्य बन रहा है${win2 ? `। इसके उपरांत **${win2.dateRange}** में भी सुंदर योग रहेगा।` : '।'}

### 2. ज्योतिषीय स्थिति व वाहन सुख
- **चतुर्थ भाव (वाहन व सुख स्थान)**: चतुर्थेश **${fourthLord?.lord || 'चतुर्थेश'}** ${fourthLord?.planet ? `भाव ${fourthLord.planet.house} में [${fourthLord.planet.dignity}]` : ''} स्थित होकर उत्तम वाहन सुख व यात्रा सुरक्षा प्रदान कर रहे हैं।
- **वाहन कारक शुक्र व मंगल**: शुक्र (ऐश्वर्य व आराम) और मंगल (यांत्रिक मजबूती व नियंत्रण) की स्थिति वाहन की उपयोगिता व स्थायित्व के लिए अनुकूल है।
- **षोडशांश (D16) वाहन चक्र**: षोडशांश वर्ग के अनुसार नया वाहन आपके पारिवारिक जीवन में आनंद, सुखद आवागमन और मानसिक संतोष लेकर आएगा।

### 3. वर्तमान दशा व गोचर
- वर्तमान **${currentMahadasha}** महादशा में **${currentAntardasha}** अंतर्दशा चतुर्थ भाव व लाभ भाव को सक्रिय कर रही है, जिससे बजट और पसंद के अनुसार वाहन चयन में आसानी होगी।

### 4. सरल एवं व्यावहारिक परामर्श
- वाहन की डिलीवरी शुक्रवार अथवा बुधवार के शुभ मुहूर्त में लेना विशेष शुभ रहेगा।
- वाहन में श्री गणेश जी की छोटी प्रतिमा या लाल/सफेद रक्षा सूत्र स्थापित करें।
- बजट व बीमा (Insurance) की सभी शर्तों को व्यवस्थित रूप से देखकर ही अग्रिम भुगतान करें।`;
    }

    return `🕉️ **Vehicle Purchase & Conveyance Consultation for ${nativeId}** — *${currentMonthYearStr}*

### 1. Most Favorable Timing for Vehicle Purchase
Based on your natal planetary alignments, your **strongest window for purchasing a vehicle is between ${win1 ? win1.dateRange : antardashaEndDate} (during ${win1 ? win1.label : currentAntardasha + ' Antardasha'})**${win2 ? `, with an additional supportive window around **${win2.dateRange} (${win2.label})**.` : '.'}

### 2. Astrological Factors for Vahana Sukha
- **4th House (Conveyance & Comforts)**: 4th Lord **${fourthLord?.lord || 'Lord'}** ${fourthLord?.planet ? `placed in House ${fourthLord.planet.house} [${fourthLord.planet.dignity}]` : ''} confers strong conveyance happiness, safety, and long-term utility.
- **Natural Karakas (Venus & Mars)**: Venus (luxury, comfort, aesthetic design) and Mars (engine mechanics, safety, and driving control) are harmoniously disposed.
- **D16 Shodashamsha Alignment**: Confirms that this vehicle acquisition brings convenience, peace of mind, and family comfort.

### 3. Dasha & Transit Activation
- Running **${currentMahadasha}-${currentAntardasha}** actively supports major lifestyle upgrades and mobility investments.

### 4. Practical & Auspicious Guidance
- Friday or Wednesday during Shukla Paksha (waxing moon) are especially auspicious for taking delivery.
- Ensure thorough pre-delivery inspection (PDI) and comprehensive insurance coverage before completing final paperwork.`;
  }

  // 4. PROPERTY / REAL ESTATE CONSULTATION
  if (classification.primaryDomain === 'property' || classification.eventType === 'PROPERTY_PURCHASE') {
    const fourthLord = primaryHousesLords.find(h => h.house === 4);
    const win1 = eventTimingAnalysis?.candidateWindow1;
    const win2 = eventTimingAnalysis?.candidateWindow2;

    if (isHi) {
      return `🕉️ **भूमि, भवन एवं संपत्ति परामर्श (${nativeId})** — *${currentMonthYearStr}*

### 1. संपत्ति / गृह क्रय का शुभ समय
आपकी कुंडली में अचल संपत्ति, फ्लैट या भूमि क्रय का **सबसे मजबूत समय ${win1 ? win1.dateRange : antardashaEndDate} (${win1 ? win1.label : currentAntardasha + ' अंतर्दशा'})** के मध्य निर्मित हो रहा है${win2 ? `। द्वितीयक अवसर **${win2.dateRange}** के आसपास प्राप्त होगा।` : '।'}

### 2. गृह व भूमि योग
- **चतुर्थ भाव (गृह सुख व भूमि)**: चतुर्थेश **${fourthLord?.lord || 'चतुर्थेश'}** ${fourthLord?.planet ? `भाव ${fourthLord.planet.house} में [${fourthLord.planet.dignity}]` : ''} स्थित हैं, जो स्थायी निवास और संपत्ति संचय को मजबूती देते हैं।
- **भूमि कारक मंगल व शनि**: मंगल (जमीन व निर्माण) और शनि (दीर्घकालिक संपत्ति व स्थायित्व) की अनुकूलता संपत्ति में मूल्य वृद्धि दर्शाती है।
- **चतुर्थांश (D4) चक्र**: अचल संपत्ति में किया गया निवेश भविष्य में वित्तीय सुरक्षा और पारिवारिक स्थिरता प्रदान करेगा।

### 3. सावधानियां व व्यावहारिक परामर्श
- संपत्ति के सभी कानूनी दस्तावेज, रजिस्ट्री एवं मालिकाना हक की जांच किसी विशेषज्ञ से अवश्य कराएं।
- किसी भी प्रकार के जल्दबाजी में लिए गए सौदे से बचें।`;
    }

    return `🕉️ **Real Estate & Property Consultation for ${nativeId}** — *${currentMonthYearStr}*

### 1. Favorable Timing for Property Acquisition
The planetary configurations highlight the **strongest window for real estate or home purchase between ${win1 ? win1.dateRange : antardashaEndDate} (during ${win1 ? win1.label : currentAntardasha + ' Antardasha'})**${win2 ? `, with a secondary favorable window around **${win2.dateRange}**.` : '.'}

### 2. Astrological Real Estate Foundation
- **4th House & Land Governance**: 4th Lord **${fourthLord?.lord || 'Lord'}** ${fourthLord?.planet ? `in House ${fourthLord.planet.house} [${fourthLord.planet.dignity}]` : ''} strengthens residential stability and fixed asset ownership.
- **Bhoomi Karakas (Mars & Saturn)**: Mars provides property building power while Saturn ensures long-term valuation endurance.
- **D4 Chaturthamsha**: Confirms stable growth in tangible fixed assets over time.

### 3. Practical Guidance
- Conduct thorough title searches and legal due diligence before releasing advance token payments.`;
  }

  // 5. GENERAL DOMAIN CONSULTATION
  const mainLord = primaryHousesLords[0];
  const primaryKaraka = karakas[0];
  const mainSav = savPoints[0]?.points || 28;

  if (isHi) {
    return `🕉️ **ज्योतिषीय परामर्श: ${classification.primaryDomain.toUpperCase()} (${nativeId})** — *${currentMonthYearStr}*

1. **प्रत्यक्ष उत्तर**:
   आपकी **${lagnaSign}** लग्न कुंडली में **${classification.primaryDomain}** के संदर्भ में ${evidencePkg.balanceOfEvidenceVerdict}

2. **कुंडली का आधार**:
   - **लग्न एवं लग्नेश**: **${lagnaSign}** लग्न के स्वामी **${lagnaLord}** ${lagnaLordPlacement ? `भाव ${lagnaLordPlacement.house} में ${lagnaLordPlacement.dignity}` : ''} स्थित हैं।
   - **मुख्य भाव**: ${primaryHousesLords.map(hl => `${hl.house}वें भाव (${hl.sign}) के स्वामी ${hl.lord}${hl.planet ? ` भाव ${hl.planet.house} में स्थित हैं` : ''}`).join('। ')}।
   - **कारक ग्रह**: ${karakas.map(k => `${k.name} (${k.significance})`).join(', ')}।
   - **दशमांश / वर्ग प्रभाव**: ${evidencePkg.relevantVargaFindings}।

3. **दशा एवं गोचर समय चक्र**:
   - **वर्तमान सक्रिय समय**: **${currentMahadasha}** महादशा में **${currentAntardasha}** अंतर्दशा (${antardashaStartDate} से ${antardashaEndDate})।
   - **पूर्वानुमान**: ${evidencePkg.timingForecastSummary}

4. **व्यावहारिक मार्गदर्शन**:
   - स्पष्ट योजना बनाकर आगे बढ़ें और महत्वपूर्ण पहलों को सक्रिय अंतर्दशा खिड़कियों के साथ संरेखित करें।`;
  }

  return `🕉️ **Astrological Consultation: ${classification.primaryDomain.toUpperCase()} for ${nativeId}** — *${currentMonthYearStr}*

1. **Direct Consultation Answer**:
   Based on your **${lagnaSign}** ascendant and verified planetary configurations, ${evidencePkg.balanceOfEvidenceVerdict}

2. **Astrological Foundation**:
   - **Ascendant & Core Strength**: **${lagnaSign}** Lagna ruled by **${lagnaLord}** ${lagnaLordPlacement ? `(placed in House ${lagnaLordPlacement.house} with ${lagnaLordPlacement.dignity} status)` : ''}.
   - **Key Bhava Matrix**: ${primaryHousesLords.map(hl => `${formatOrdinal(hl.house)} House (${hl.sign}) Lord **${hl.lord}**${hl.planet ? ` in House ${hl.planet.house} [${hl.planet.dignity}]` : ''}`).join('; ')}.
   - **Natural Karakas**: ${karakas.map(k => `${k.name} (${k.significance})`).join(', ')}.
   - **Divisional Refinement (${evidencePkg.relevantVargaChartName})**: ${evidencePkg.relevantVargaFindings}.

3. **Timing & Dasha Execution Windows**:
   - **Active Period**: Running **${currentMahadasha}** Mahadasha with **${currentAntardasha}** Antardasha (${antardashaStartDate} to ${antardashaEndDate}).
   - **Transit Currents**: Saturn in ${gocharSaturn.sign} and Jupiter in ${gocharJupiter.sign}.
   - **Forecast Summary**: ${evidencePkg.timingForecastSummary}

4. **Practical Guidance**:
   - Align major career or lifestyle transitions with your most favorable calculated sub-period windows and maintain disciplined daily execution.`;
}
